import React, {
  useEffect,
  useMemo,
  useRef,
  useCallback,
  useState,
} from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  useGridApiRef,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridPinnedColumns,
  GridSortModel,
  GridFilterModel,
} from '@mui/x-data-grid-premium';
import StyledDataGrid from '../../components/DataGrid/DataGridStyles';
import { CustomGridFooter } from '../../components/DataGrid/CustomGridFooter';
import ReportGenerationModal from '../../components/Modal/ReportGenerationModal/ReportGenerationModal';
import { useTableSearch } from '../../hooks/useTableSearch';
import { useViewButtons } from '../../components/Views/useViewButtons';
import { useSyncOperations } from '../../hooks/useSyncOperations';
import { useViewManagement } from '../../hooks/useViewManagement/useViewManagement';
import { PortfolioView } from '../../hooks/useViewManagement/types';
import AutosizeColumnsToggle from '../../components/DataGrid/AutosizeColumnsToggle';
import { PAGE_TYPES } from '../../constants/pageTypes';
import GroupToggle from '../../components/DataGrid/GroupToggle';
import Toolbar from '../../components/Toolbar/Toolbar';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SyncIcon from '@mui/icons-material/Sync';
import LayersIcon from '@mui/icons-material/Layers';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import EmptyState from '../../components/EmptyState/EmptyState';
import SaveViewDialog from '../../components/Views/SaveViewDialog';
import SavedViewsPanel from '../../components/Views/SavedViewsPanel';
import ActiveViewIndicator from '../../components/Views/ActiveViewIndicator';
import ViewContextMenu from '../../components/Views/ViewContextMenu';
import ManageGroupsModal from '../../components/Groups/ManageGroupsModal';
import { usePortfolioData } from './usePortfolioData';
import { usePortfolioColumns } from './usePortfolioColumns';
import { PortfolioDataTable } from '../../types/generatedTypes';
import './PortfolioTable.scss';

import {
  transformDataForGrouping,
  getTreeDataPath,
  convertGridColumnsToGroupColumns,
  getRowIdAsString,
} from '../../components/Groups/groupUtils';
import { useGroupingColDef } from '../../components/Groups/useManageGroups';
import { GroupedRowData } from '../../components/Groups/types';
import {
  createStateChangeHandler,
  useFilteredSelectionModel,
} from '../../components/Groups/gridStateHelper';
import { useGroupCheckboxHandler } from '../../components/Groups/useGroupCheckboxHandler';

// Re-export for backwards compatibility
export type PortfolioSavedView = PortfolioView;

export const PortfolioTablePage: React.FC = () => {
  const apiRef = useGridApiRef();

  // Ref to preserve grid state during remounting
  const preservedStateRef = useRef<{
    pinnedColumns?: GridPinnedColumns;
    sortModel?: GridSortModel;
    filterModel?: GridFilterModel;
    columnVisibility?: Record<string, boolean>;
  } | null>(null);

  // Ref to hold current display data for "Mark All" functionality
  const displayDataRef = useRef<GroupedRowData[]>([]);

  // State to track current display data for selection handler
  const [currentDisplayData, setCurrentDisplayData] = useState<
    GroupedRowData[]
  >([]);

  const { isSyncingPortfolioAndCustodian, syncPortfolioCustodianFromIllio } =
    useSyncOperations();

  // First get basic portfolio data
  const {
    portfolioData,
    loading,
    selectedRows,
    isReportModalOpen,
    isGeneratingReport,
    autosizeColumns,
    setAutosizeColumns,
    columnVisibilityModel,
    setColumnVisibilityModel,
    fetchPortfolioData,
    handleRowSelectionChange,
    handleOpenReportModal,
    handleCloseReportModal,
    handleSubmitReport,
    handleExport,
    handleSyncPortfolioCustodianFromIllio,
    expandGroups,
    // Groups
    groups,
    setGroups,
    isGroupsModalOpen,
    isGroupingEnabled,
    setIsGroupingEnabled,
    availableRowsForGroups,
    handleOpenGroupsModal,
    handleCloseGroupsModal,
    handleSaveGroups,
  } = usePortfolioData({
    apiRef,
    syncPortfolioCustodianFromIllio,
    displayDataRef,
    displayData: currentDisplayData,
  });

  const { filteredData, searchTerm, handleSearch } =
    useTableSearch<PortfolioDataTable>(portfolioData);

  // Function to capture current grid state before remounting
  const captureGridState = React.useCallback(() => {
    if (apiRef.current) {
      preservedStateRef.current = {
        pinnedColumns: apiRef.current.getPinnedColumns?.() || {
          left: [],
          right: [],
        },
        sortModel: apiRef.current.getSortModel?.() || [],
        filterModel: apiRef.current.getFilterModel?.() || { items: [] },
        columnVisibility: apiRef.current.getColumnVisibilityModel?.() || {},
      };
    }
  }, []);

  // Wrapper function to capture state before toggling groups
  const handleToggleGrouping = React.useCallback(
    (enabled: boolean) => {
      // Capture current state before the toggle causes remounting
      captureGridState();
      // Then toggle grouping
      setIsGroupingEnabled(enabled);
    },
    [captureGridState, setIsGroupingEnabled]
  );

  // Add view management hook
  const viewManager = useViewManagement({
    pageType: PAGE_TYPES.PORTFOLIO,
    apiRef,
    searchTerm,
    selectedRows,
    autosizeColumns,
    displayData: filteredData, // Will be updated below after baseDisplayData calculation
    handleSearch,
    setSelectedRows: (rows: string[]) => {
      const model = { ids: new Set(rows), type: 'include' as const };
      handleRowSelectionChange(model);
    },
    setAutosizeColumns,
    isGroupingEnabled,
    setIsGroupingEnabled,
    groups,
    setGroups,
    rowIdKey: '_id',
  });

  // Apply selected rows filtering when active view has selections
  const baseDisplayData =
    viewManager.filteredDisplayData as PortfolioDataTable[];

  // Transform data for grouping
  const displayData = useMemo(() => {
    return transformDataForGrouping(baseDisplayData, groups, isGroupingEnabled);
  }, [baseDisplayData, groups, isGroupingEnabled]);

  // Calculate actual data row count (excluding group headers)
  const actualRowCount = useMemo(() => {
    return displayData.filter(row => !row.isGroup).length;
  }, [displayData]);

  // Update ref and state whenever displayData changes
  useEffect(() => {
    displayDataRef.current = displayData;
    setCurrentDisplayData(displayData);
  }, [displayData]);

  // Create filtered rowSelectionModel to preserve selections during search
  const getRowIdCallback = useCallback(
    (row: PortfolioDataTable) => getRowIdAsString(row),
    []
  );
  const rowSelectionModel = useFilteredSelectionModel(
    selectedRows,
    displayData,
    getRowIdCallback
  );

  // Generate columns using the custom hook
  const { columns } = usePortfolioColumns({
    autosizeColumns,
    isGroupingEnabled,
  });

  // Get grouping column definition
  const groupingColDef = useGroupingColDef(isGroupingEnabled);

  // Group checkbox click handler
  const handleCellClick = useGroupCheckboxHandler({
    displayData,
    selectedRows,
    expandGroups,
    onSelectionChange: handleRowSelectionChange,
    getRowId: row => String(row._id),
  });

  useEffect(() => {
    fetchPortfolioData();
  }, [fetchPortfolioData]);

  // Restore preserved state after remount (when grouping is toggled)
  useEffect(() => {
    // Only restore if we have preserved state and the grid is ready
    if (preservedStateRef.current && apiRef.current) {
      // Use a timeout to ensure grid is fully mounted
      const timeout = setTimeout(() => {
        const state = preservedStateRef.current!;

        // Restore sort model
        if (state.sortModel && apiRef.current) {
          apiRef.current.setSortModel(state.sortModel);
        }

        // Restore filter model
        if (state.filterModel && apiRef.current) {
          apiRef.current.setFilterModel(state.filterModel);
        }

        // Column visibility is now controlled via state, no need to restore here

        // Handle pinned columns with protected column adjustments
        if (state.pinnedColumns && apiRef.current) {
          // Always ensure checkbox column is first, then add group columns if grouping is enabled
          const protectedColumns = [GRID_CHECKBOX_SELECTION_COL_DEF.field];

          if (isGroupingEnabled) {
            protectedColumns.push(GRID_TREE_DATA_GROUPING_FIELD, 'group_name');
          }

          // Extract user columns from preserved state by filtering out all protected columns
          const userColumns = (state.pinnedColumns.left || []).filter(
            col => !protectedColumns.includes(col)
          );

          // Always rebuild with protected columns first, then user columns
          const correctedLeft = [...protectedColumns, ...userColumns];

          apiRef.current.setPinnedColumns({
            left: correctedLeft,
            right: state.pinnedColumns.right || [],
          });
        }

        // Clear preserved state after restoration
        preservedStateRef.current = null;
      }, 0);

      return () => clearTimeout(timeout);
    }
  }, [isGroupingEnabled]);

  // Get view buttons from hook
  const viewButtons = useViewButtons({
    onSaveView: viewManager.openSaveDialog,
    onOpenSavedViews: viewManager.openSavedViewsPanel,
    loading: loading,
    disabled: false,
  });

  const selectedCount = selectedRows.length;

  // Helper function to track view modifications
  const updateViewModifications = React.useCallback(() => {
    const modifications = viewManager.compareWithAppliedView();
    viewManager.setViewModifications(modifications);
  }, [viewManager]);

  // Define columns configuration for Groups modal
  const groupColumns = useMemo(
    () => convertGridColumnsToGroupColumns(columns),
    [columns]
  );

  return (
    <Box data-testid="portfolio-page" className="portfolioTableContainer">
      <Box className="portfolioToolbarSection">
        <div data-testid="portfolio-search-input">
          <Toolbar
            onSearch={handleSearch}
            placeholder="Search portfolios..."
            searchTerm={searchTerm}
            disabled={loading || isGeneratingReport}
            onExport={handleExport}
            buttons={[
              ...viewButtons,
              {
                variant: 'text',
                color: 'secondary',
                label: `Generate Report${
                  selectedCount > 0 ? ` (${selectedCount})` : ''
                }`,
                onClick: handleOpenReportModal,
                icon: isGeneratingReport ? (
                  <CircularProgress size={20} />
                ) : (
                  <CloudUploadIcon />
                ),
                disabled: loading || isGeneratingReport || selectedCount === 0,
                tooltip: 'Generate report for selected portfolios',
              },
              {
                variant: 'text',
                label: 'Sync from Illio',
                color: 'secondary',
                onClick: handleSyncPortfolioCustodianFromIllio,
                icon: isSyncingPortfolioAndCustodian ? (
                  <CircularProgress size={20} />
                ) : (
                  <SyncIcon />
                ),
                disabled: loading || isSyncingPortfolioAndCustodian,
                tooltip: 'Synchronize portfolio data from Illio',
              },
              {
                variant: 'text',
                label: 'Groups',
                color: 'secondary',
                onClick: handleOpenGroupsModal,
                icon: <LayersIcon />,
                disabled: loading,
                tooltip: 'Organize portfolios into groups',
              },
            ]}
          />
        </div>

        <Box className="portfolioControlsRow">
          <Box className="portfolioControlsLeft">
            <AutosizeColumnsToggle
              apiRef={apiRef}
              checked={autosizeColumns}
              onChange={setAutosizeColumns}
            />
            <GroupToggle
              checked={isGroupingEnabled}
              onChange={handleToggleGrouping}
              disabled={groups.length === 0}
            />
          </Box>
          {viewManager.activeView && (
            <ActiveViewIndicator
              view={viewManager.activeView}
              modifications={viewManager.viewModifications}
              onClose={viewManager.clearActiveView}
              onUpdateView={viewManager.updateCurrentView}
            />
          )}
        </Box>
      </Box>

      <Box className="portfolioGridContainer">
        {loading ? (
          <Box className="portfolioLoadingContainer">
            <CircularProgress />
          </Box>
        ) : actualRowCount === 0 ? (
          <EmptyState
            icon={
              <FolderOpenIcon sx={{ fontSize: 80, color: 'text.secondary' }} />
            }
            title="No Portfolio Data"
            description="Sync portfolio data from Illio to begin managing portfolios"
          />
        ) : (
          <StyledDataGrid
            data-testid="portfolio-data-grid"
            key={isGroupingEnabled ? 'grouped-grid' : 'normal-grid'}
            apiRef={apiRef}
            rows={displayData}
            rowCount={actualRowCount}
            columns={columns}
            getRowId={row => row._id}
            getRowClassName={params =>
              params.row.isGroup ? 'group-row' : 'data-row'
            }
            checkboxSelection
            isRowSelectable={params => !params.row.isGroup}
            disableRowSelectionOnClick
            rowSelectionModel={rowSelectionModel}
            treeData={isGroupingEnabled}
            getTreeDataPath={isGroupingEnabled ? getTreeDataPath : undefined}
            groupingColDef={groupingColDef}
            defaultGroupingExpansionDepth={isGroupingEnabled ? 1 : undefined}
            columnVisibilityModel={columnVisibilityModel}
            initialState={{
              pinnedColumns: {
                left: [
                  GRID_CHECKBOX_SELECTION_COL_DEF.field,
                  ...(isGroupingEnabled
                    ? [GRID_TREE_DATA_GROUPING_FIELD, 'group_name']
                    : []),
                ],
              },
            }}
            slots={{
              footerRowCount: CustomGridFooter,
            }}
            slotProps={{
              footerRowCount: {
                isGroupingEnabled,
                groups,
              },
            }}
            onCellClick={handleCellClick}
            onRowSelectionModelChange={handleRowSelectionChange}
            onSortModelChange={() => {
              updateViewModifications();
            }}
            onFilterModelChange={() => {
              updateViewModifications();
            }}
            onColumnVisibilityModelChange={newModel => {
              setColumnVisibilityModel(newModel);
              updateViewModifications();
            }}
            onPinnedColumnsChange={() => {
              if (viewManager.activeView) {
                requestAnimationFrame(() => {
                  updateViewModifications();
                });
              }
            }}
            onStateChange={createStateChangeHandler(apiRef, isGroupingEnabled)}
          />
        )}
      </Box>

      <ReportGenerationModal
        open={isReportModalOpen}
        onClose={handleCloseReportModal}
        onSubmit={handleSubmitReport}
        title="Generate Portfolio Report"
        submitText="Generate Report"
        selectedPortfolios={selectedRows.map(id => {
          const portfolio = portfolioData.find(p => p.id === id);
          return {
            portfolio_id: portfolio?.portfolio_id || id,
            inception_date: portfolio?.inception_date || null,
            client_name: portfolio?.client_name || null,
          };
        })}
      />

      <SaveViewDialog
        open={viewManager.isSaveViewDialogOpen}
        onClose={viewManager.closeSaveDialog}
        onSave={viewManager.saveView}
        editingView={viewManager.editingView}
        editMode={viewManager.editMode}
        currentSearchTerm={searchTerm}
        autosizeColumns={autosizeColumns}
        isGroupingEnabled={isGroupingEnabled}
        selectedRows={selectedRows}
        savedViews={viewManager.savedViews}
        columnFilters={viewManager.getColumnFilters()}
        sortModel={viewManager.getSortModel()}
        columnVisibility={viewManager.getColumnVisibility()}
        pinnedColumns={viewManager.getPinnedColumns()}
        columns={columns}
      />

      <SavedViewsPanel
        views={viewManager.savedViews}
        onViewClick={viewManager.handleViewClick}
        onSaveClick={viewManager.openSaveDialog}
        onMenuClick={viewManager.handleMenuClick}
        activeView={viewManager.activeView}
        open={viewManager.isSavedViewsPanelOpen}
        onClose={viewManager.closeSavedViewsPanel}
      />

      <ViewContextMenu
        anchorEl={viewManager.contextMenuAnchor}
        open={Boolean(viewManager.contextMenuAnchor)}
        onClose={viewManager.handleCloseContextMenu}
        onApply={viewManager.handleApplyView}
        onRename={viewManager.handleRenameView}
        onUpdate={viewManager.handleUpdateView}
        onDelete={viewManager.handleDeleteView}
      />

      <ManageGroupsModal
        open={isGroupsModalOpen}
        onClose={handleCloseGroupsModal}
        onSave={handleSaveGroups}
        availableRows={availableRowsForGroups}
        existingGroups={groups}
        title="Manage Groups"
        columns={groupColumns}
        hiddenColumns={['leading_currency', 'group_name']}
      />
    </Box>
  );
};
